/**
 * Theme preference: explicit light/dark, or follow the operating system.
 *
 * The active choice is cached in localStorage for instant first paint. When a
 * user is signed in, the same value is stored on the server per client device.
 */

import { api } from '@/api/client'
import { getClientDeviceId } from '@/lib/clientDevice'
import { THEME_STORAGE_KEY } from '@/lib/themeBoot'

export type ThemePreference = 'light' | 'dark' | 'system'

// Shared with the boot module, which reads it before this module is evaluated.
const STORAGE_KEY = THEME_STORAGE_KEY
const THEME_COLOR_LIGHT = '#F1F5F8'
const THEME_COLOR_DARK = '#111315'

export interface ClientDevicePreference {
  client_device_id: string
  appearance: ThemePreference
  updated_at: string | null
}

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function getStoredTheme(): ThemePreference {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    if (isThemePreference(value)) return value
  } catch {
    // Private browsing may refuse storage.
  }
  return 'system'
}

export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'light') return 'light'
  if (preference === 'dark') return 'dark'
  return systemPrefersDark() ? 'dark' : 'light'
}

function updateThemeColor(resolved: 'light' | 'dark'): void {
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta === null) return
  meta.setAttribute('content', resolved === 'dark' ? THEME_COLOR_DARK : THEME_COLOR_LIGHT)
}

/** Applies the preference to the document root and browser chrome. */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement

  if (preference === 'system') root.removeAttribute('data-theme')
  else root.dataset.theme = preference

  updateThemeColor(resolveTheme(preference))
}

export function setStoredTheme(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    // Still apply for this session even when storage is unavailable.
  }
  applyTheme(preference)
}

export async function loadThemeFromServer(signal?: AbortSignal): Promise<ThemePreference | null> {
  const clientDeviceId = getClientDeviceId()

  try {
    const response = await api.get<ClientDevicePreference>(
      `/client-preferences/${encodeURIComponent(clientDeviceId)}`,
      undefined,
      signal,
    )
    return isThemePreference(response.appearance) ? response.appearance : null
  } catch {
    return null
  }
}

export async function saveThemeToServer(preference: ThemePreference): Promise<void> {
  await api.patch<ClientDevicePreference>('/client-preferences', {
    client_device_id: getClientDeviceId(),
    appearance: preference,
  })
}

let systemListener: (() => void) | null = null

/** Call once before React mounts so the first paint uses the cached palette. */
export function initTheme(): ThemePreference {
  const preference = getStoredTheme()
  applyTheme(preference)

  if (systemListener === null) {
    systemListener = (): void => {
      if (getStoredTheme() === 'system') applyTheme('system')
    }
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', systemListener)
  }

  return preference
}
